import {
  parseContractComplianceError,
  uploadJustificationDocumentRequest,
} from 'api/requests/contract-compliance-requests';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENERAL__ERROR_MESSAGE } from 'translations/constants';
import { IJustificationDocument, IJustificationPayload } from 'types/contract-compliance/IContractCompliance';
import { getContractComplianceErrorMessage } from '../utils';

/**
 * Optional justification of a save: free text plus one supporting document. Uploading does not save anything;
 * the document id is only sent with the save that follows (backend note).
 */
export const useJustification = () => {
  const [t] = useTranslation();
  const [text, setText] = useState('');
  const [document, setDocument] = useState<IJustificationDocument | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);
  // Bumped on reset and on every new upload, so an upload that finishes after either is ignored.
  const uploadSession = useRef(0);

  const reset = useCallback(() => {
    uploadSession.current += 1;
    setText('');
    setDocument(null);
    setIsUploading(false);
    setUploadErrorMessage(null);
  }, []);

  const upload = useCallback(
    async (file: File) => {
      uploadSession.current += 1;
      const session = uploadSession.current;
      setIsUploading(true);
      setUploadErrorMessage(null);
      try {
        const uploaded = await uploadJustificationDocumentRequest(file);
        if (session === uploadSession.current) setDocument(uploaded);
      } catch (error) {
        if (session === uploadSession.current) {
          setUploadErrorMessage(
            getContractComplianceErrorMessage(t, parseContractComplianceError(error), GENERAL__ERROR_MESSAGE),
          );
        }
      } finally {
        if (session === uploadSession.current) setIsUploading(false);
      }
    },
    [t],
  );

  const clearDocument = useCallback(() => {
    uploadSession.current += 1;
    setDocument(null);
    setIsUploading(false);
    setUploadErrorMessage(null);
  }, []);

  /** Whitespace-only text counts as none (same rule as the API). */
  const toPayload = useCallback(
    (): IJustificationPayload => ({
      justificationText: text.trim() ? text.trim() : null,
      justificationDocumentId: document?.id ?? null,
    }),
    [text, document],
  );

  return {
    text,
    setText,
    document,
    clearDocument,
    isUploading,
    uploadErrorMessage,
    setUploadErrorMessage,
    upload,
    reset,
    toPayload,
  };
};

export type UseJustificationResult = ReturnType<typeof useJustification>;
